import { DateInput } from "./DateInput";
import { useState } from "react";
import { useAuth } from "../lib/auth";
import { Lock, Star, Trash2, Plus } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { formatMoney } from "../lib/settings";
import { toast } from "sonner";
import PhotoUpload from "./PhotoUpload";
import { DocumentUploadModal } from "./DocumentUploadModal";

import { tr, lbl, LANG } from "../lib/i18n";
import { useBranches, branchName } from "../lib/branches";
const COST_CATS = [
  "Transportation", "Repair & Maintenance", "Detailing",
  "Registration", "Insurance", "Customs/Clearing", "Spare Parts", "Other",
];
const EMPTY_COST = { category: "Transportation", amount: "", date: "", vendor: "", note: "" };

function Row({ label, value, strong }) {
  return (
    <tr style={{ borderBottom: strong ? "2px solid var(--text)" : "1px solid var(--divider-soft)" }}>
      <td style={{ padding: "10px 0", fontSize: 13, color: "var(--text-muted)" }}>{label}</td>
      <td className="tabular" style={{ padding: "10px 0", textAlign: "end", fontSize: 14, fontWeight: strong ? 600 : 500 }}>{value}</td>
    </tr>
  );
}

export function ManagersOnly() {
  return (
    <div className="panel mt-6" data-testid="costs-locked">
      <div className="panel-body flex items-center gap-4" style={{ padding: 32 }}>
        <div style={{ width: 44, height: 44, background: "var(--surface)", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Lock size={18} strokeWidth={1.75} />
        </div>
        <div>
          <div style={{ fontSize: 16, fontWeight: 600 }}>{tr("x.available_to_managers_only")}</div>
          <div className="text-sm" style={{ color: "var(--text-muted)" }}>{tr("x.costs_profit_and_margin_are_visible_to_a")}</div>
        </div>
      </div>
    </div>
  );
}

function CostModal({ id, onClose, onSaved }) {
  const [f, setF] = useState(EMPTY_COST);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const save = async () => {
    try {
      const payload = { ...f, amount: Number(f.amount || 0), date: f.date || new Date().toISOString().slice(0, 10) };
      await api.post(`/vehicles/${id}/costs`, payload);
      toast.success(tr("x.cost_added"));
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="panel w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="cost-modal">
        <div className="panel-title"><div className="kicker">{tr("x.add_cost_line")}</div></div>
        <div className="panel-body grid gap-3">
          <select value={f.category} onChange={set("category")} className="input" data-testid="cost-category">
            {COST_CATS.map((c) => <option key={c} value={c}>{lbl(c)}</option>)}
          </select>
          <input data-testid="cost-amount" type="number" step="0.001" placeholder={tr("x.amount_2")} value={f.amount} onChange={set("amount")} className="input" />
          <DateInput data-testid="cost-date" type="date" value={f.date} onChange={set("date")} className="input" />
          <input data-testid="cost-vendor" placeholder={tr("x.vendor")} value={f.vendor} onChange={set("vendor")} className="input" />
          <div className="flex gap-2 justify-end mt-2">
            <button className="btn-secondary" onClick={onClose} data-testid="cost-cancel">{tr("x.cancel")}</button>
            <button className="btn-primary" onClick={save} data-testid="submit-cost">{tr("x.add_cost")}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function CostsTab({ v, settings, canEdit, reload }) {
  const [open, setOpen] = useState(false);
  const c = v.computed || {};
  const remove = async (costId) => {
    if (!window.confirm(tr("x.remove_this_cost_line"))) return;
    try { await api.delete(`/vehicles/${v.id}/costs/${costId}`); toast.success(tr("x.removed")); reload(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const lines = v.cost_lines || [];
  return (
    <div className="mt-6 grid gap-[2px]" data-testid="costs-tab"
      style={{ background: "var(--divider)", border: "1px solid var(--divider)", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%,340px), minmax(0,1fr)))" }}>
      <div style={{ background: "#fff", padding: 20 }}>
        <div className="kicker mb-2">{tr("x.computed_totals")}</div>
        <table style={{ width: "100%", borderCollapse: "collapse" }}><tbody>
          <Row label={tr("x.purchase_price")} value={formatMoney(v.purchase_price, settings)} />
          <Row label={tr("x.additional_costs")} value={formatMoney(c.total_additional_cost, settings)} />
          <Row label={tr("x.total_vehicle_cost")} value={formatMoney(c.total_vehicle_cost, settings)} strong />
          <Row label={tr("x.final_selling_price")} value={formatMoney(v.final_selling_price, settings)} />
          <Row label={tr("x.gross_profit")} value={formatMoney(c.gross_profit, settings)} />
          <Row label={tr("x.net_profit")} value={formatMoney(c.net_profit, settings)} strong />
          <Row label={tr("x.margin")} value={c.margin_percentage != null ? `${c.margin_percentage}%` : "—"} />
        </tbody></table>
      </div>
      <div style={{ background: "#fff", padding: 20 }}>
        <div className="flex items-center justify-between mb-2">
          <div className="kicker">{tr("x.cost_lines")}{lines.length})</div>
          {canEdit && <button className="btn-primary" onClick={() => setOpen(true)} data-testid="add-cost-btn"><Plus size={14} strokeWidth={1.75} /> {tr("x.add_cost_line")}</button>}
        </div>
        {lines.length === 0 && <div className="text-sm opacity-60 py-3">{tr("x.no_cost_lines_yet")}</div>}
        <table style={{ width: "100%", borderCollapse: "collapse" }}><tbody>
          {lines.map((cl) => (
            <tr key={cl.id} style={{ borderBottom: "1px solid var(--divider-soft)" }} data-testid={`cost-line-${cl.id}`}>
              <td style={{ padding: "10px 0", fontSize: 13 }}>{lbl(cl.category)}<div className="text-xs opacity-60">{cl.date} · {cl.vendor || "—"}</div></td>
              <td className="tabular" style={{ textAlign: "end", fontSize: 14, fontWeight: 500 }}>{formatMoney(cl.amount, settings)}</td>
              <td style={{ textAlign: "end", width: 40 }}>
                {canEdit && <button className="btn-ghost" onClick={() => remove(cl.id)} aria-label={tr("x.remove")} data-testid={`remove-cost-${cl.id}`}><Trash2 size={14} /></button>}
              </td>
            </tr>
          ))}
        </tbody></table>
      </div>
      {open && <CostModal id={v.id} onClose={() => setOpen(false)} onSaved={() => { setOpen(false); reload(); }} />}
    </div>
  );
}

export function PhotosTab({ v, canEdit, reload }) {
  const run = (fn) => async (...a) => { try { await fn(...a); reload(); } catch (e) { toast.error(formatApiError(e)); } };
  const add = run((p) => api.post(`/vehicles/${v.id}/photos`, { url: p.url, thumbnail_url: p.thumbnail_url, key: p.key, variants: p.variants }));
  const del = run(async (pid) => { if (window.confirm(tr("x.delete_this_photo"))) await api.delete(`/vehicles/${v.id}/photos/${pid}`); });
  const primary = run((pid) => api.patch(`/vehicles/${v.id}/photos/reorder`, { order: v.photos.map((p) => p.id), primary_id: pid }));
  const photos = v.photos || [];
  return (
    <div className="panel mt-6" data-testid="photos-tab">
      <div className="panel-body grid gap-4">
        {canEdit && <PhotoUpload onUploaded={add} />}
        {photos.length === 0 && <div className="text-sm opacity-60">{tr("x.no_photos_yet")}</div>}
        <div className="grid gap-[2px]" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", background: "var(--divider)" }}>
          {photos.map((p) => (
            <div key={p.id} className="relative group" style={{ aspectRatio: "4/3", background: "#fff" }} data-testid={`photo-${p.id}`}>
              <img src={p.variants?.thumbnail || p.thumbnail_url || p.url} alt="" loading="lazy" decoding="async" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              {p.is_primary && <span className="absolute top-2 start-2 text-[10px] uppercase tracking-widest px-2 py-1" style={{ background: "var(--p)", color: "var(--p-ink)" }}>{tr("x.primary")}</span>}
              {canEdit && (
                <div className="absolute bottom-2 end-2 flex gap-1">
                  {!p.is_primary && <button className="btn-secondary" style={{ minHeight: 32, padding: "0 8px" }} onClick={() => primary(p.id)} title={tr("x.make_primary")} data-testid={`photo-primary-${p.id}`}><Star size={14} /></button>}
                  <button className="btn-secondary" style={{ minHeight: 32, padding: "0 8px" }} onClick={() => del(p.id)} title={tr("x.delete")} data-testid={`photo-delete-${p.id}`}><Trash2 size={14} /></button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function DocumentsTab({ v, onChange }) {
  const docs = v.documents || [];
  const { can } = useAuth();
  const [open, setOpen] = useState(false);
  return (
    <div className="panel mt-6" data-testid="documents-tab">
      <div className="panel-body">
        {can("vehicles", "edit") && <button className="btn-primary mb-3" onClick={() => setOpen(true)} data-testid="vehicle-doc-upload-btn">{tr("x.upload_document")}</button>}
        {open && <DocumentUploadModal vehicleId={v.id} onClose={() => setOpen(false)} onSaved={(nv) => { setOpen(false); onChange?.(nv); }} />}
        {docs.length === 0 && <div className="text-sm opacity-60">{tr("x.no_documents_attached")}</div>}
        {docs.map((d) => (
          <div key={d.id} className="py-3" style={{ borderBottom: "1px solid var(--divider-soft)" }}>
            <a href={d.url} target="_blank" rel="noreferrer" className="text-sm font-medium" style={{ color: "var(--accent-700)" }} data-testid={`doc-link-${d.id}`}>{d.name}</a>
            <div className="text-xs opacity-60">{d.doc_type || "—"} {tr("x.uploaded")} {d.uploaded_at?.slice(0, 10)}{d.expiry_date ? tr("x.expires", { p0: d.expiry_date }) : ""}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function ActivityTab({ v }) {
  const items = (v.status_history || []).slice().reverse();
  const { all } = useBranches();
  const bn = (id, fallback) => branchName(all.find((b) => b.id === id), LANG) || fallback || "—";
  return (
    <div className="panel mt-6" data-testid="activity-tab">
      <div className="panel-body">
        {items.length === 0 && <div className="text-sm opacity-60">{tr("x.no_status_changes_yet")}</div>}
        {items.map((h, i) => (
          <div key={i} className="py-3 text-sm" style={{ borderBottom: "1px solid var(--divider-soft)" }}>
            {h.kind === "transfer" ? (
              <span data-testid="history-transfer"><span className="font-medium">{tr("br.transfer")}</span>: <span className="opacity-60">{bn(h.from_branch_id, h.from_branch)}</span> <span className="rtl-flip inline-block">→</span> <span className="font-medium">{bn(h.to_branch_id, h.to_branch)}</span></span>
            ) : (
              <><span className="opacity-60">{lbl(h.from_status) || "—"}</span> <span className="rtl-flip inline-block">→</span> <span className="font-medium">{lbl(h.to_status)}</span></>
            )}
            <div className="text-xs opacity-60">{h.changed_at?.replace("T", " ").slice(0, 16)} · {h.changed_by_name}{h.kind === "transfer" ? (h.note ? ` · ${h.note}` : "") : (h.reason ? ` · ${h.reason}` : "")}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
